import { PROMO_TIMEZONE } from '../data/promotion'

/**
 * Calendar-week promotion logic. The deadline is always the upcoming Sunday at
 * 11:59:59 PM America/New_York, computed from the real clock. It never depends
 * on page load, sessions, cookies or the visitor's own timezone, and it handles
 * daylight saving time through Intl instead of a fixed UTC offset.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

interface EasternParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  weekday: number
}

const formatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PROMO_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  weekday: 'short',
  hourCycle: 'h23',
})

export function getCurrentEasternTime(at: Date = new Date()): EasternParts {
  const parts = formatter.formatToParts(at)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0'
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    second: Number(get('second')),
    weekday: WEEKDAYS.indexOf(get('weekday')),
  }
}

/** Offset (ms) between Eastern wall-clock time and UTC at a given instant. */
function easternOffset(at: Date): number {
  const p = getCurrentEasternTime(at)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(at.getTime() / 1000) * 1000
}

/** Converts an Eastern wall-clock time to a real instant, DST-safe. */
function easternWallTimeToDate(year: number, month: number, day: number, h: number, m: number, s: number): Date {
  const guess = Date.UTC(year, month - 1, day, h, m, s)
  let instant = guess - easternOffset(new Date(guess))
  // Second pass settles the offset on DST transition days.
  instant = guess - easternOffset(new Date(instant))
  return new Date(instant)
}

/** The upcoming Sunday at 11:59:59 PM Eastern. On Sunday, that is tonight. */
export function getNextSundayDeadline(now: Date = new Date()): Date {
  const et = getCurrentEasternTime(now)
  const daysUntilSunday = (7 - et.weekday) % 7
  const target = new Date(Date.UTC(et.year, et.month - 1, et.day + daysUntilSunday))
  return easternWallTimeToDate(
    target.getUTCFullYear(),
    target.getUTCMonth() + 1,
    target.getUTCDate(),
    23,
    59,
    59,
  )
}

export interface ActiveSale {
  /** Full name, e.g. "250 Years Sale". */
  name: string
  /** Short form used in the countdown line, e.g. "250 Years". */
  short: string
  tagline: string
}

/** Nth weekday (0 = Sun) of a month, day of month. n = -1 means last. */
function nthWeekday(year: number, month: number, weekday: number, n: number): number {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay()
    return 1 + ((weekday - first + 7) % 7) + (n - 1) * 7
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const last = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay()
  return lastDay - ((last - weekday + 7) % 7)
}

const md = (month: number, day: number) => month * 100 + day

/**
 * The sale name switches by itself on the Eastern calendar, every year, with
 * no manual edits. Each holiday runs only the days leading up to it, so the
 * reason for the sale is always real at the moment it is shown.
 */
export function getActiveSale(now: Date = new Date()): ActiveSale {
  const et = getCurrentEasternTime(now)
  const today = md(et.month, et.day)
  const y = et.year
  const memorial = nthWeekday(y, 5, 1, -1)
  const labor = nthWeekday(y, 9, 1, 1)
  const thanksgiving = nthWeekday(y, 11, 4, 4)
  const blackFridayStart = thanksgiving - 13

  const inRange = (from: number, to: number) => today >= from && today <= to

  if (inRange(md(1, 1), md(1, 15)))
    return { name: 'New Year Sale', short: 'New Year', tagline: 'New year, new gear. 60% off for the people who keep America running.' }
  if (inRange(md(5, memorial - 7), md(5, memorial)))
    return { name: 'Memorial Day Sale', short: 'Memorial Day', tagline: 'Honoring those who gave everything. 60% off for the people who keep America running.' }
  if (inRange(md(6, 28), md(7, 5)))
    return { name: '4th of July Sale', short: '4th of July', tagline: 'Celebrating American independence. 60% off for the people who keep America running.' }
  if (labor - 7 >= 1 ? inRange(md(9, labor - 7), md(9, labor)) : inRange(md(8, 31 + labor - 7), md(9, labor)))
    return { name: 'Labor Day Sale', short: 'Labor Day', tagline: 'Built for the American worker. 60% off for the people who keep America running.' }
  if (inRange(md(11, 2), md(11, 11)))
    return { name: 'Veterans Day Sale', short: 'Veterans Day', tagline: 'Honoring the men and women who served. 60% off for the people who keep America running.' }
  if (inRange(md(11, Math.max(12, blackFridayStart)), md(11, 30)))
    return { name: 'Black Friday Sale', short: 'Black Friday', tagline: 'The biggest deal of the year. 60% off for the people who keep America running.' }
  if (inRange(md(12, 1), md(12, 24)))
    return { name: 'Christmas Sale', short: 'Christmas', tagline: 'The gift every worker actually uses. 60% off for the people who keep America running.' }
  if (inRange(md(12, 25), md(12, 31)))
    return { name: 'Year-End Sale', short: 'Year-End', tagline: 'Close out the year with better gear. 60% off for the people who keep America running.' }
  if (y === 2026)
    return { name: '250 Years Sale', short: '250 Years', tagline: 'America turns 250. 60% off for the people who keep it running.' }
  return { name: 'American Work Gear Sale', short: 'Work Gear Sale', tagline: '60% off for the people who keep America running.' }
}

export interface WeeklyPromoState {
  days: number
  hours: number
  minutes: number
  seconds: number
  /** Short urgency line, adapted to the Eastern weekday. */
  headline: string
  /** Optional second line (Friday only). */
  subline?: string
}

export function calculateRemainingTime(now: Date = new Date()): WeeklyPromoState {
  const deadline = getNextSundayDeadline(now)
  const total = Math.max(0, Math.floor((deadline.getTime() - now.getTime()) / 1000))
  const weekday = getCurrentEasternTime(now).weekday
  const short = getActiveSale(now).short

  let headline = `This week's ${short} price ends Sunday`
  let subline: string | undefined
  if (weekday === 5) subline = 'Only 2 days left'
  if (weekday === 6) headline = `This week's ${short} price ends tomorrow`
  if (weekday === 0) headline = `This week's ${short} price ends tonight`

  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
    headline,
    subline,
  }
}
